import moment from "moment";
import { useState, useEffect, useRef } from "react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { Calendar, X } from "lucide-react";
import { ucFirst } from "../../lib/helper-support";

type onChangeInputFunc = (e: string) => void;

type InputType = {
  placeholder?: string;
  label?: string;
  id?: string;
  value?: string; // YYYY-MM-DD OR YYYY-MM-DD to YYYY-MM-DD
  defaultValue?: string; // NEW: initial reset value
  error?: string | null;
  disabled?: boolean;
  onChangeInput?: onChangeInputFunc;
  inputClass?: string;
  mode?: "single" | "range";
  clearable?: boolean; // NEW
};

const API_FORMAT = "YYYY-MM-DD";
const DISPLAY_FORMAT = "DD/MM/YYYY";

const DatePickerField = ({
  placeholder,
  label,
  id,
  value,
  defaultValue,
  error,
  onChangeInput,
  disabled = false,
  inputClass,
  mode = "single",
  clearable = false,
}: InputType) => {
  const Id = id ?? label;

  const [date, setDate] = useState<Date | Date[] | null>(null);

  /** store initial default once */
  const initialValueRef = useRef<string | undefined>(defaultValue);

  /** sync external value → flatpickr */
  useEffect(() => {
    if (!value) {
      setDate(null);
      return;
    }

    if (mode === "single") {
      setDate(moment(value, API_FORMAT).toDate());
    }

    if (mode === "range") {
      const [from, to] = value.split(" to ");
      if (from && to) {
        setDate([
          moment(from, API_FORMAT).toDate(),
          moment(to, API_FORMAT).toDate(),
        ]);
      }
    }
  }, [value, mode]);

  /** flatpickr → formatted output */
  const onChangeDate = (dates: Date[]) => {
    if (!dates || dates.length === 0) return;

    if (mode === "single") {
      const selected = dates[0];
      setDate(selected);
      onChangeInput?.(moment(selected).format(API_FORMAT));
    }

    if (mode === "range" && dates.length === 2) {
      setDate(dates);
      onChangeInput?.(
        `${moment(dates[0]).format(API_FORMAT)} to ${moment(dates[1]).format(API_FORMAT)}`
      );
    }
  };

  /** reset to initial default value */
  const clearSelection = () => {
    const resetValue = initialValueRef.current ?? "";

    onChangeInput?.(resetValue);

    if (!resetValue) {
      setDate(null);
      return;
    }

    if (mode === "single") {
      setDate(moment(resetValue, API_FORMAT).toDate());
    }

    if (mode === "range") {
      const [from, to] = resetValue.split(" to ");
      if (from && to) {
        setDate([
          moment(from, API_FORMAT).toDate(),
          moment(to, API_FORMAT).toDate(),
        ]);
      }
    }
  };

  /** display-only formatting */
  const displayValue = () => {
    if (!value) return placeholder ?? "dd/mm/yyyy";

    if (mode === "single") {
      return moment(value, API_FORMAT).format(DISPLAY_FORMAT);
    }

    const [from, to] = value.split(" to ");
    return `${moment(from, API_FORMAT).format(DISPLAY_FORMAT)} to ${moment(to, API_FORMAT).format(DISPLAY_FORMAT)}`;
  };

  return (
    <label  className="label">
      {label && <span>{label}</span>}

      <div
        className={`relative flex items-center justify-between w-full input ${inputClass}
          ${error && "border-red-500"}
          ${disabled && "text-light-black"}`}
      >
        <span className="truncate">{displayValue()}</span>

        <div className="flex items-center gap-2 z-20">
          {clearable && value && !disabled && (
            <button
              type="button"
              onClick={clearSelection}
              className="text-gray-400 hover:text-red-500 cursor-pointer w-fit"
            >
              <X size={14} />
            </button>
          )}
          <Calendar size={16} />
        </div>

        <div className="absolute inset-0">
          <Flatpickr
            value={date || undefined}
            onChange={onChangeDate}
            options={{
              dateFormat: "Y-m-d",
              allowInput: true,
              enableTime: false,
              mode,
            }}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={disabled}
          />
        </div>
      </div>

      {error && (
        <p className="my-1 text-[13px] text-red-500 font-normal">
          {ucFirst(error)}
        </p>
      )}
    </label>
  );
};

export default DatePickerField;